import React, { useState, useEffect } from "react";
import { api, fmt, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfidenceBadge } from "@/components/ConfidenceBadge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, UserCog, Database, ScrollText, Landmark, ShieldCheck, MapPin, Trash2, Plus } from "lucide-react";

const PROFILE_FIELDS = [
  ["home_base_salary", "Home base salary", "number"], ["fixed_cash", "Fixed cash", "number"],
  ["bonus_pct", "Bonus %", "number"], ["dependants", "Dependants", "number"],
  ["assignment_months", "Assignment months", "number"], ["housing_norm_pct", "Housing norm %", "number"],
  ["savings_pct", "Savings %", "number"], ["family_status", "Family status", "text"],
  ["tax_filing_status", "Tax filing status", "text"], ["tax_policy", "Tax policy", "text"],
  ["grade", "Grade", "text"], ["nationality", "Nationality", "text"],
];

export default function Admin() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [sources, setSources] = useState([]);
  const [taxRules, setTaxRules] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [audit, setAudit] = useState([]);
  const [editEmp, setEditEmp] = useState(null);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadAll = () => {
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/sources").then((r) => setSources(r.data));
    api.get("/tax-rules").then((r) => setTaxRules(r.data));
    api.get("/policies").then((r) => setPolicies(r.data));
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const openEdit = async (emp) => {
    const { data } = await api.get(`/employees/${emp.employee_id}`);
    setEditEmp(data);
    setFields({ ...data.profile });
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const parsed = {};
      for (const [k, , type] of PROFILE_FIELDS) {
        if (fields[k] !== undefined && fields[k] !== "") parsed[k] = type === "number" ? Number(fields[k]) : fields[k];
      }
      if (!parsed.children_ages) parsed.children_ages = editEmp.profile?.children_ages || [];
      if (!parsed.home_currency) parsed.home_currency = editEmp.profile?.home_currency;
      await api.put(`/employees/${editEmp.employee_id}/profile`, { fields: parsed });
      setEditEmp(null); loadAll();
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/sources/upload", fd); loadAll(); } finally { setUploading(false); }
  };

  const activePolicy = policies.find((p) => p.active);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-up">
      <div className="text-xs uppercase tracking-[0.2em] text-[#B45309] font-semibold">Governance</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Admin Console</h1>
      <p className="text-muted-foreground mt-2">Signed in as {ROLE_LABELS[user.role]}. Manage the employee master, policy, source registry and tax rules.</p>

      <Tabs defaultValue="employees" className="mt-6">
        <TabsList className="bg-white border flex-wrap h-auto">
          <TabsTrigger value="employees" data-testid="admin-tab-employees"><UserCog className="h-4 w-4 mr-1.5" />Employee Master</TabsTrigger>
          <TabsTrigger value="sources" data-testid="admin-tab-sources"><Database className="h-4 w-4 mr-1.5" />Source Registry</TabsTrigger>
          <TabsTrigger value="policy" data-testid="admin-tab-policy"><ShieldCheck className="h-4 w-4 mr-1.5" />Policy</TabsTrigger>
          <TabsTrigger value="basket" data-testid="admin-tab-basket"><Database className="h-4 w-4 mr-1.5" />COLA Basket</TabsTrigger>
          <TabsTrigger value="tax" data-testid="admin-tab-tax"><Landmark className="h-4 w-4 mr-1.5" />Tax Rules</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText className="h-4 w-4 mr-1.5" />Audit</TabsTrigger>
        </TabsList>

        {/* Employees */}
        <TabsContent value="employees">
          <Card className="bg-white overflow-hidden" data-testid="admin-employee-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-400"><tr>
                <th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Grade</th><th className="text-left px-4 py-3">Business Unit</th>
                <th className="text-left px-4 py-3">Profile</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {employees.map((e, i) => (
                  <tr key={e.employee_id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-4 py-3 font-mono text-xs">{e.employee_id}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{e.name}</td>
                    <td className="px-4 py-3">{e.grade || e.profile?.grade || "—"}</td>
                    <td className="px-4 py-3">{e.business_unit}</td>
                    <td className="px-4 py-3">
                      <ProfileStatus emp={e} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`edit-emp-${e.employee_id}`}>Complete / Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Sources */}
        <TabsContent value="sources">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground max-w-2xl">Licensed, public, government and admin-approved datasets. Mercer figures display only after a valid licensed dataset is imported.</p>
            <label className="shrink-0">
              <input type="file" className="hidden" onChange={upload} data-testid="source-upload-input" />
              <span className="inline-flex items-center gap-2 rounded-lg bg-slate-50 text-slate-400 px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-slate-900-800">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload licensed dataset
              </span>
            </label>
          </div>
          <Card className="bg-white overflow-x-auto" data-testid="admin-source-registry-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-400"><tr>
                <th className="text-left px-4 py-3">Organisation</th><th className="text-left px-4 py-3">Dataset</th>
                <th className="text-left px-4 py-3">Licence</th><th className="text-left px-4 py-3">Period</th>
                <th className="text-left px-4 py-3">Retrieved</th><th className="text-left px-4 py-3">Confidence</th>
              </tr></thead>
              <tbody>
                {sources.map((sc, i) => (
                  <tr key={sc.source_id} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`source-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{sc.organisation}</td>
                    <td className="px-4 py-3">{sc.dataset}</td>
                    <td className="px-4 py-3"><span className="text-xs uppercase font-semibold text-slate-500">{sc.licence_category}</span></td>
                    <td className="px-4 py-3">{sc.reference_period}</td>
                    <td className="px-4 py-3 font-mono text-xs">{sc.retrieved_at ? sc.retrieved_at.slice(0, 10) : "not loaded"}</td>
                    <td className="px-4 py-3"><ConfidenceBadge level={sc.confidence} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Policy */}
        <TabsContent value="policy">
          <Card className="p-6 bg-white" data-testid="admin-policy-versions">
            <h3 className="font-display font-bold text-slate-900 mb-1">Active Policy v{activePolicy?.version}</h3>
            <p className="text-sm text-muted-foreground mb-4">Version-controlled. The 30% target bonus and negative-COLA treatment are configurable, never hard-coded.</p>
            {activePolicy && <PolicyEditor policy={activePolicy} onSaved={loadAll} />}
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Version history</div>
              {policies.map((p) => (
                <div key={p.version} className="text-sm flex items-center gap-3 py-1">
                  <span className={`font-mono ${p.active ? "text-emerald-600 font-bold" : "text-slate-500"}`}>v{p.version}</span>
                  {p.active && <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">ACTIVE</span>}
                  <span className="text-muted-foreground text-xs">{p.created_at?.slice(0, 16).replace("T", " ")} · {p.created_by}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* COLA basket editor */}
        <TabsContent value="basket"><BasketEditor /></TabsContent>

        {/* Tax rules */}
        <TabsContent value="tax">
          <Card className="p-6 bg-white" data-testid="admin-tax-rules-editor">
            <h3 className="font-display font-bold text-slate-900 mb-1">Deterministic Tax Engine v{taxRules?.engine_version}</h3>
            <p className="text-sm text-muted-foreground mb-4">Progressive brackets by country. Taxes are computed by these deterministic rules — never by an LLM.</p>
            <div className="grid md:grid-cols-2 gap-3">
              {taxRules && Object.entries(taxRules.rules).map(([cc, rule]) => (
                <div key={cc} className="rounded-lg border p-3" data-testid={`tax-rule-${cc}`}>
                  <div className="font-semibold text-slate-900 text-sm">{cc} — {rule.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">Std deduction: {rule.standard_deduction.toLocaleString()} {rule.currency} · Dependant allowance: {rule.dependant_allowance.toLocaleString()}</div>
                  <div className="text-xs mt-1 font-mono">{rule.brackets.length} bands · social: {(taxRules.social_security[cc]?.rate * 100).toFixed(1)}%</div>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">State / provincial / local tax library</div>
              <div className="grid md:grid-cols-2 gap-2">
                {taxRules && Object.entries(taxRules.state_local || {}).map(([k, v]) => (
                  <div key={k} className="text-sm rounded border p-2" data-testid={`state-tax-${k}`}>
                    <span className="font-medium text-slate-700">{k}</span> — {v.name}{" "}
                    <span className="text-xs text-muted-foreground">({v.type})</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Totalisation agreements (must be confirmed before exemption)</div>
              <div className="flex flex-wrap gap-2">
                {taxRules?.totalisation.map((t, i) => (
                  <span key={i} className="text-xs rounded-full bg-slate-100 px-3 py-1 font-medium">{t.a}↔{t.b} {t.in_force ? "✓ in force" : "✗"}</span>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit">
          <Card className="bg-white overflow-x-auto" data-testid="admin-audit-log">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-400"><tr>
                <th className="text-left px-4 py-3">Time</th><th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Detail</th><th className="text-left px-4 py-3">By</th>
              </tr></thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={a.event_id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-4 py-3 font-mono text-xs">{a.at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-3"><span className="text-xs uppercase font-semibold text-essred">{a.action}</span></td>
                    <td className="px-4 py-3 text-slate-400">{a.detail}</td>
                    <td className="px-4 py-3 text-xs">{a.by} ({a.role})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={!!editEmp} onOpenChange={(o) => !o && setEditEmp(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-profile-dialog">
          <DialogHeader><DialogTitle>Complete profile — {editEmp?.name}</DialogTitle></DialogHeader>
          {editEmp && !editEmp.profile_complete && (
            <div className="text-sm rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800">
              Missing: {editEmp.missing_fields?.join(", ")}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label>Home currency</Label>
              <Select value={fields.home_currency || ""} onValueChange={(v) => setFields({ ...fields, home_currency: v })}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="field-home_currency"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent className="bg-white">{["USD", "INR", "GBP", "SGD", "AED", "CHF", "JPY", "NGN", "EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {PROFILE_FIELDS.map(([k, lbl, type]) => (
              <div key={k}>
                <Label>{lbl}</Label>
                <Input data-testid={`field-${k}`} type={type} value={fields[k] ?? ""} className="mt-1.5"
                  onChange={(e) => setFields({ ...fields, [k]: e.target.value })} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label>Children ages (comma-separated)</Label>
              <Input data-testid="field-children_ages" value={(fields.children_ages || []).join(",")} className="mt-1.5"
                onChange={(e) => setFields({ ...fields, children_ages: e.target.value.split(",").map((x) => x.trim()).filter(Boolean).map(Number) })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveProfile} disabled={saving} className="bg-slate-50 text-slate-400" data-testid="save-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BasketEditor() {
  const [b, setB] = useState(null);
  const [versions, setVersions] = useState([]);
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/basket").then((r) => { setB(r.data); setCity(Object.keys(r.data.indices || {})[0] || ""); });
    api.get("/basket/versions").then((r) => setVersions(r.data));
  };
  useEffect(load, []);
  if (!b || !b.weights) return <div className="p-6 text-muted-foreground">Loading basket…</div>;
  const sum = Object.values(b.weights).reduce((a, c) => a + Number(c), 0);
  const setW = (k, v) => setB({ ...b, weights: { ...b.weights, [k]: v } });
  const setI = (k, v) => setB({ ...b, indices: { ...b.indices, [city]: { ...b.indices[city], [k]: v } } });
  const num = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, Number(v)]));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/basket", {
        weights: num(b.weights),
        indices: Object.fromEntries(Object.entries(b.indices).map(([ck, cv]) => [ck, num(cv)])),
      });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6 bg-white" data-testid="admin-basket-editor">
      <h3 className="font-display font-bold text-slate-900 mb-1">COLA Basket v{b.version}</h3>
      <p className="text-sm text-muted-foreground mb-4">Category weights (must sum to 1.0) and per-city price indices. Saving creates a new version.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {Object.keys(b.weights).map((k) => (
          <div key={k}>
            <Label className="text-xs capitalize">{k.replace(/_/g, " ")}</Label>
            <Input type="number" step="0.01" data-testid={`basket-weight-${k}`} value={b.weights[k]} className="mt-1" onChange={(e) => setW(k, e.target.value)} />
          </div>
        ))}
      </div>
      <div className={`mt-2 text-sm ${Math.abs(sum - 1) < 0.01 ? "text-emerald-500" : "text-essred"}`}>Weights sum: {sum.toFixed(2)} {Math.abs(sum - 1) < 0.01 ? "✓" : "(must equal 1.00)"}</div>
      <div className="mt-5">
        <Label>City price indices (New York = 100)</Label>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="mt-1 bg-white max-w-xs" data-testid="basket-city-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{Object.keys(b.indices).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
        {city && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-3">
            {Object.keys(b.weights).map((k) => (
              <div key={k}>
                <Label className="text-xs capitalize">{k.replace(/_/g, " ")}</Label>
                <Input type="number" step="0.1" value={b.indices[city]?.[k] ?? 0} className="mt-1" onChange={(e) => setI(k, e.target.value)} />
              </div>
            ))}
          </div>
        )}
      </div>
      <Button onClick={save} disabled={saving || Math.abs(sum - 1) > 0.01} className="mt-5 bg-[#F5A623] hover:bg-[#e0961a] text-[#0A0E14]" data-testid="save-basket-btn">{saving ? "Saving…" : "Save as new version"}</Button>
      <div className="mt-5">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Version history</div>
        {versions.map((v) => (
          <div key={v.version} className="text-sm py-0.5">
            <span className={`font-mono ${v.active ? "text-emerald-500 font-bold" : "text-slate-400"}`}>v{v.version}</span>{" "}
            <span className="text-xs text-muted-foreground">{v.created_at?.slice(0, 16).replace("T", " ")} · {v.created_by}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ProfileStatus({ emp }) {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get(`/employees/${emp.employee_id}`).then((r) => setStatus(r.data.profile_complete)); }, [emp.employee_id]);
  if (status === null) return <span className="text-xs text-slate-400">…</span>;
  return status
    ? <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-semibold">Complete</span>
    : <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-semibold">Incomplete</span>;
}

function PolicyEditor({ policy, onSaved }) {
  const [cfg, setCfg] = useState(policy.config);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/policy/active", { config: {
        home_housing_norm_pct: Number(cfg.home_housing_norm_pct),
        negative_cola_policy: cfg.negative_cola_policy,
        negative_cola_cap_pct: Number(cfg.negative_cola_cap_pct),
        target_bonus_pct: Number(cfg.target_bonus_pct),
      } });
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label>Home housing norm %</Label>
        <Input type="number" step="0.01" value={cfg.home_housing_norm_pct} className="mt-1.5" data-testid="policy-housing-norm"
          onChange={(e) => setCfg({ ...cfg, home_housing_norm_pct: e.target.value })} />
      </div>
      <div>
        <Label>Target bonus %</Label>
        <Input type="number" step="0.01" value={cfg.target_bonus_pct} className="mt-1.5" data-testid="policy-bonus"
          onChange={(e) => setCfg({ ...cfg, target_bonus_pct: e.target.value })} />
      </div>
      <div>
        <Label>Negative COLA policy</Label>
        <Select value={cfg.negative_cola_policy} onValueChange={(v) => setCfg({ ...cfg, negative_cola_policy: v })}>
          <SelectTrigger className="mt-1.5 bg-white" data-testid="policy-neg-cola"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="full">Apply full negative COLA</SelectItem>
            <SelectItem value="capped">Apply capped negative COLA</SelectItem>
            <SelectItem value="floor">Zero-floor protection</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Negative COLA cap %</Label>
        <Input type="number" step="0.01" value={cfg.negative_cola_cap_pct} className="mt-1.5" data-testid="policy-cap"
          onChange={(e) => setCfg({ ...cfg, negative_cola_cap_pct: e.target.value })} />
      </div>
      <div className="sm:col-span-2">
        <Button onClick={save} disabled={saving} className="bg-essred hover:bg-essred-700 text-white" data-testid="save-policy-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save as new version
        </Button>
      </div>
    </div>
  );
}
